import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useMediaQuery } from 'usehooks-ts'


import { useAuth } from '@features/auth'
import { useErrorInfoModal, useModalStore } from '@features/modal'
import { useAddressStore } from '@features/shipping-methods'
import { AddressItemType } from '@features/shipping-methods/types'

import { useSlots } from '@widgets/slots'

import {
  useMyAddressesQuery,
  useZonesForGeoPointLazyQuery,
} from '@shared/generated'
import Button from '@shared/ui/button'

import AddressItem from './item'

import styles from './styles.module.scss'

function AddressListSkeleton () {
  return (
    <SkeletonTheme>
      <Skeleton
        count={4}
        width={'100%'}
        height={30}
        style={{ borderRadius: 6, marginBottom: 20 }}
      />
    </SkeletonTheme>
  )
}

export default function AddressList () {
  const isTablet = useMediaQuery('(max-width: 991px)')
  const { t: tc } = useTranslation('common')
  const { t: te } = useTranslation('errors')
  const { openErrorInfoModal } = useErrorInfoModal()
  const { data, loading } = useMyAddressesQuery({
    variables: { showPickupShops: false },
  })
  const { closeModal } = useModalStore()
  const { openSlotsModal } = useSlots()
  const {
    selectedAddress,
    setSelectedAddress,
    savedAddresses,
    setDeliveryType,
  } = useAddressStore()
  const [ pendingAddress, setPendingAddress ] = useState<AddressItemType | null>(
    null,
  )

  const { requireRegistration } = useAuth()
  const [ fetchZonesForGeopoint ] = useZonesForGeoPointLazyQuery()

  if (loading) return <AddressListSkeleton />

  const listToRender = !requireRegistration
    ? data?.myAddresses || []
    : savedAddresses?.filter((el) => !el?.shop_id) || []

  if (!listToRender) return null

  const handleSubmit = async () => {
    if (pendingAddress) {
      try {
        const res = await fetchZonesForGeopoint({
          variables: {
            point: {
              latitude: pendingAddress.customerGeoPoint?.latitude,
              longitude: pendingAddress.customerGeoPoint?.longitude,
            },
          },
        })

        const zones = res.data?.zonesForGeoPoint
        const foundShopId = zones?.[0]?.shop_id

        if (foundShopId) {
          setSelectedAddress({
            ...pendingAddress,
            shop_id: foundShopId,
          })
        }
      }
      catch (error) {
        console.log(error)
        openErrorInfoModal({
          description: te('address_not_found'),
        })
      }
    }
    setDeliveryType('slot')
    closeModal('delivery')
    openSlotsModal()
  }

  return (
    <>
      <div className={styles.list}>
        {listToRender.map((item, index) => (
          <AddressItem
            key={`${item?.id}-${index}`}
            address={item as AddressItemType}
            selected={
              pendingAddress?.id === item?.id ||
              (!pendingAddress && selectedAddress?.id === item?.id)
            }
            onSelect={() => setPendingAddress(item)}
          />
        ))}
      </div>
      {listToRender.length ? (
        <Button
          disabled={!pendingAddress}
          type="button"
          size={!isTablet ? 'large' : 'medium'}
          onClick={handleSubmit}
        >
          {tc('choose')}
        </Button>
      ) : null}
    </>
  )
}
